import { useEffect, useRef } from "react";

interface PageHeaderProps {
	description?: string;
	title: string;
}

/**
 * Sticky page chrome matching the dashboard `PageHeader`: title + optional
 * supporting line above the scrollable content. On small viewports, includes
 * `#mobile-header` height in `--page-header-height`.
 */
export const PageHeader = ({ description, title }: PageHeaderProps) => {
	const headerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const headerElement = headerRef.current;
		if (!headerElement) return;

		const updateHeight = () => {
			const isMobile =
				window.matchMedia?.("(max-width: 1023px)")?.matches ?? false;
			let mobileHeaderHeight = 0;

			if (isMobile) {
				const mobileHeader = document.querySelector("#mobile-header");
				if (mobileHeader) {
					mobileHeaderHeight = (mobileHeader as HTMLElement).offsetHeight;
				}
			}

			document.documentElement.style.setProperty(
				"--page-header-height",
				`${headerElement.offsetHeight + mobileHeaderHeight}px`,
			);
		};

		requestAnimationFrame(updateHeight);
		const resizeObserver = new ResizeObserver(updateHeight);
		resizeObserver.observe(headerElement);

		const mobileHeader = document.querySelector("#mobile-header");
		if (mobileHeader) {
			resizeObserver.observe(mobileHeader);
		}

		window.addEventListener("resize", updateHeight);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", updateHeight);
		};
	}, [description, title]);

	return (
		<div
			className="top-0 z-20 border-b border-secondary bg-primary px-4 py-4 text-primary shadow-header lg:sticky lg:px-12 lg:py-6"
			ref={headerRef}
		>
			<h1 className="text-lg font-semibold lg:text-xl xl:text-2xl/8">
				{title}
			</h1>
			{description && (
				<p className="mt-1 text-sm text-tertiary xl:text-md/7">{description}</p>
			)}
		</div>
	);
};
